import {
  ref,
  uploadBytesResumable,
  type UploadTaskSnapshot,
} from 'firebase/storage';
import { ensureUser, getStorageOrThrow } from './firebase';
import type { PhotoRef } from './types';

const ALLOWED = /^image\/(jpe?g|png|webp|heic|heif)$/i;
const MAX_BYTES = 10 * 1024 * 1024;

export async function uploadPhotos(
  jobId: string,
  files: File[],
  startOrder: number,
  onProgress?: (loaded: number, total: number) => void,
): Promise<PhotoRef[]> {
  const user = await ensureUser();
  const storage = getStorageOrThrow();
  const totalBytes = files.reduce((s, f) => s + f.size, 0);
  let loadedBytes = 0;

  const results = await Promise.all(
    files.map((file, i) => {
      if (!ALLOWED.test(file.type)) {
        return Promise.reject(
          new Error(`קובץ לא נתמך: ${file.name}`),
        );
      }
      if (file.size > MAX_BYTES) {
        return Promise.reject(
          new Error(`התמונה ${file.name} גדולה מ-10MB`),
        );
      }
      const order = startOrder + i;
      const safeName = file.name.replace(/[^\w.\-]+/g, '_');
      const path = `users/${user.uid}/jobs/${jobId}/photos/${String(
        order,
      ).padStart(3, '0')}-${safeName}`;
      const r = ref(storage, path);
      const task = uploadBytesResumable(r, file, {
        contentType: file.type,
      });

      let lastTransferred = 0;
      return new Promise<PhotoRef>((resolve, reject) => {
        task.on(
          'state_changed',
          (snap: UploadTaskSnapshot) => {
            const delta = snap.bytesTransferred - lastTransferred;
            lastTransferred = snap.bytesTransferred;
            loadedBytes += delta;
            onProgress?.(loadedBytes, totalBytes);
          },
          (err) => reject(err),
          () => resolve({ path, order }),
        );
      });
    }),
  );

  results.sort((a, b) => a.order - b.order);
  return results;
}
